import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {Link, useNavigate} from 'react-router-dom';

import { carouselRedirectList } from '../../../utils/choices';
import {Grid, Stack, Typography} from '@mui/material';
import {useEffect, useState} from 'react';

// shadows
const FLAT_SHADOW = `
  inset 0 0 0 rgba(0,0,0,0),
  inset 0 0 0 rgba(255,255,255,0)
`;
const FINAL_SHADOW = `
  inset 6px 6px 8px rgba(0,0,0,0.4),
  inset -6px -6px 8px rgba(255,255,255,1)
`;
const HOVER_SHADOW = `
  inset -3px -3px 4px rgba(0, 0, 0, 0.2),
  inset 6px 6px 8px rgba(255, 255, 255, 1)
`;

export function StaggerList({
  children,
  base = 200,          // mount base delay (ms)
  step = 120,          // mount per-item step (ms)
  hoverBase = 0,       // hover base delay (ms)
  hoverStep = 60,      // hover per-item step (ms)
  sx,
  ...props
}) {
  const items = React.Children.toArray(children);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <Stack
      sx={{
        alignItems: 'stretch',
        // default (non-hover) stagger vars
        '--stagger-base': `${base}ms`,
        '--stagger-step': `${step}ms`,
        // when hovering the whole list, use different timings
        '&:hover': {
          '--stagger-base': `${hoverBase}ms`,
          '--stagger-step': `${hoverStep}ms`,
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& *': { transition: 'none !important', animation: 'none !important' },
        },
        ...sx,
      }}
      {...props}
    >
      {items.map((child, i) => (
        <Box
          key={i}
          sx={{
            width: '100%',
            '--i': i, // index for this row
            '& > *': {
              // entrance: flat -> final, with default stagger
              opacity: mounted ? 1 : 0,
              boxShadow: mounted ? FINAL_SHADOW : FLAT_SHADOW,
              transitionProperty: 'opacity, box-shadow',
              transitionTimingFunction: 'ease-out',
              transitionDuration: '480ms',
              transitionDelay: 'calc(var(--stagger-base) + var(--i) * var(--stagger-step))',

              '&:hover': {
                fontWeight: 'bolder',
                boxShadow: HOVER_SHADOW,
                transitionDelay: '0ms',
              },
            },
          }}
        >
          {child}
        </Box>
      ))}
    </Stack>
  );
}


export const ToolButton = ({tool, path}) => {
  const theme = useTheme()
  return (
    <Button
      position='relative'
      display='inline-block'
      component={Link}
      fullWidth={true}
      to={`tools/${path}`}
      sx={{
        height: {xs: '5vh', sm: '7vh'},
        borderRadius: '4px',
        fontSize: 'clamp(0.6rem, 1vw, 1rem)',
        color: theme.palette.base.rothko2,
        backgroundColor: theme.palette.base.rothko5,
      }}
    >
      {tool}
    </Button>
  )
}


export function FadeShow({
  items,                    // [{ src, alt?, onClick? }, ...]
  width = "60vw",
  height = "50vh",
  interval = 5000,          // ms between images
  duration = 800,           // ms fade time
  pauseOnHover = true,
  sx,
}) {
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  // autoplay
  React.useEffect(() => {
    if (!items?.length || items.length < 2 || paused) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % items.length), interval);
    return () => clearInterval(id);
  }, [items?.length, interval, paused]);

  const hoverProps = pauseOnHover
    ? { onMouseEnter: () => setPaused(true), onMouseLeave: () => setPaused(false) }
    : {};

  return (
    <Box
      {...hoverProps}
      sx={{
        position: "relative",
        width,
        height,
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "white",
        ...sx,
      }}
    >
      {items.map((it, i) => (
        <Box
          key={it.src ?? i}
          onClick={it.onClick}
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: it.onClick ? "pointer" : "default",
            opacity: i === index ? 1 : 0,
            transition: `opacity ${duration}ms ease-in-out`,
            willChange: "opacity",
            zIndex: i === index ? 1 : 0,
            pointerEvents: i === index ? "auto" : "none",  // ← key line
          }}
        >
          <Box
            component="img"
            src={it.src}
            alt={it.alt ?? ""}
            loading="lazy"
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",         // never upscale; only shrink
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      ))}
    </Box>
  );
}


const HomeAlt = () => {
  const [visible, setVisible] = useState([false, false, false, false, false]);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const maxSteps = carouselRedirectList.length;
  const theme = useTheme()

  useEffect(() => {
    visible.forEach((_, i) => {
      setTimeout(() => {
        setVisible((prev) => prev.map((v, idx) => (idx === i ? true : v)));
      }, i * 250); // Stagger each button by 200ms
    });
  }, []);

  const handleImageClick = (toolRedirect) => {
    navigate(toolRedirect)
  }

  setTimeout(() => {
    setActiveStep(
    activeStep === maxSteps - 1 ? 0 : activeStep + 1
    );
  }, 2000);

  return (
    <Box
      width={'100%'}
      textAlign={'center'}
      mt={{xs: '-3vh', sm: 0}}
    >
      <Box
        sx={{
          position: 'relative',
          left: '50%',
          right: '50%',
          ml: '-50vw',
          mr: '-50vw',
          width: '100vw',
          overflowX: 'clip',
        }}
      >
        <Stack
          direction={'row'}
          ml={1}
          sx={{
            position: 'relative',
            left: {xs: 0, sm: '16.67vw'},
          }}
        >
          <Typography
            sx={{
              position: 'relative',
              top: '0.3em',
              zIndex: 10,
              fontSize: '10vh',
              fontFamily: 'Monoton',
            }}
          >
            MHD
          </Typography>
          <Typography
            sx={{
              position: 'relative',
              top: '1em',
              right: '0.8em',   // independent of MHD; won’t push it
              fontSize: '8vh',
              zIndex: 11,
              color: 'darkred',
              textShadow: '5px 5px 4px rgba(0, 0, 0, 0.5)',
              fontFamily: 'Share'
            }}
          >
            web
          </Typography>
        </Stack>
        <Grid
          container
          p={1}
          rowSpacing={{ xs: 2, sm: 0 }}
          columnSpacing={{ xs: 1, sm: 1 }}
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} sm={8} sx={{ order: { xs: 1, sm: 2 } }}>
            <FadeShow
              width="100%"
              height={{ xs: '40vh', sm: '50vh' }}
              fit="contain"
              interval={4500}
              duration={800}
              sx={{ boxShadow: 5, cursor: 'pointer' }}
              items={carouselRedirectList.map((step) => ({
                src: `${process.env.PUBLIC_URL}/homepage_slides/${step.img}.jpg`,
                alt: step.img,
                onClick: () => handleImageClick(step.redirect),
              }))}
            />
          </Grid>

          {/* Left buttons — below carousel on small, left column on sm+ */}
          <Grid item xs={6} sm={2} sx={{ order: { xs: 2, sm: 1 } }}>
            <StaggerList spacing={2} base={200} step={240} sx={{ alignItems: 'stretch' }}>
              <ToolButton tool="Data Access" path="data-access" />
              <ToolButton tool="Plot1D" path="plot1d"  />
              <ToolButton tool="Plot2D" path="plot2d"  />
              <ToolButton tool="Coronal Hole Maps" path="data-products"  />
              <ToolButton tool="Time Series" path="time-series"  />
            </StaggerList>
          </Grid>

          {/* Right buttons — next to left list on small, right column on sm+ */}
          <Grid item xs={6} sm={2} sx={{ order: { xs: 2, sm: 3 } }}>
            <StaggerList spacing={2} base={200} step={240} sx={{ alignItems: 'stretch' }}>
              <ToolButton tool="Spacecraft Mapping" path="spacecraft-mapping"  />
              <ToolButton tool="White Light" path="white-light"  />
              <ToolButton tool="Coronagraphs" path="coronagraphs"  />
              <ToolButton tool="EUV & X-Rays" path="euv"  />
              <ToolButton tool="EUV Imagers" path="euv-comparison"  />
            </StaggerList>
          </Grid>
        </Grid>
      </Box>
      <Box height={20}/>
      <Box width={'60vw'} margin={'0 auto'}>
        <Typography
          component={Link}
          to={'about'}
          fontStyle={'italic'}
          textAlign={'center'}
          sx={{
            textDecoration: 'none',
            color: theme.palette.base.rothko4,
            '&:hover': {
              color: 'darkred',
            }
          }}
        >
          Welcome to our MHDweb modeling website. On this site, we have a series of coronal and heliospheric model solutions covering an interval of more than 40 years. We develop this site as a general purpose tool for exploring our global magnetohydrodynamic results.
        </Typography>
      </Box>
    </Box>
  );
}

export default HomeAlt;